import { usePermissao } from "@/lib/use-permissao";
import { Link } from "react-router";
import { ChevronRight, Stethoscope, User as UserIcon } from "lucide-react";
import { useFeature } from "@/lib/use-feature";
import { trpc } from "@/lib/trpc";
import { SpecialtyChips } from "@/components/organization/SpecialtyChips";
import { EspecialidadesDaClinica } from "@/components/organization/especialidades/EspecialidadesDaClinica";

// ─── Seção "Especialidades" (catálogo compartilhado da organização) ─

export function EspecialidadesSecao() {
  const hasClinicServices = useFeature("clinicServices");
  const meQuery = trpc.me.current.useQuery();
  const activeOrganization = meQuery.data?.activeOrganization;
  // A mesma régua do servidor (settings.manage) — dono e sócio sempre têm.
  const canManage = usePermissao("settings.manage");

  const specialtiesQuery = trpc.specialty.list.useQuery(undefined, { enabled: !canManage });
  const activeQuery = trpc.organization.activeSpecialties.useQuery(undefined, { enabled: !canManage });

  if (activeOrganization?.isPersonal) {
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 dark:bg-primary/10 dark:text-primary">
          <UserIcon className="h-6 w-6" />
        </div>
        <h3 className="max-w-md text-sm font-semibold text-zinc-900 dark:text-foreground">
          Esta é sua organização pessoal
        </h3>
        <p className="max-w-md text-[12.5px] leading-relaxed text-zinc-500 dark:text-muted-foreground">
          As especialidades da sua organização pessoal se editam em{" "}
          <Link to="/conta/perfil" className="font-medium text-brand-700 hover:underline dark:text-primary">
            Minha conta › Profissional
          </Link>
          .
        </p>
      </div>
    );
  }

  if (activeOrganization && !activeOrganization.types.includes("DENTAL_OFFICE")) {
    return (
      <div className="flex flex-col items-center gap-3 px-6 py-14 text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-brand-50 text-brand-600 dark:bg-primary/10 dark:text-primary">
          <Stethoscope className="h-6 w-6" />
        </div>
        <h3 className="max-w-md text-sm font-semibold text-zinc-900 dark:text-foreground">
          Só pra organizações com perfil Clínica
        </h3>
        <p className="max-w-md text-[12.5px] leading-relaxed text-zinc-500 dark:text-muted-foreground">
          Especialidades trazem os procedimentos da agenda — essa organização não tem o perfil
          Clínica ativo em "Organização".
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4 px-6 py-5">
      {canManage ? (
        <EspecialidadesDaClinica podeEditar />
      ) : (
        // Sem permissão de configuração: só vê quais estão ativas.
        <SpecialtyChips
          options={(specialtiesQuery.data ?? []).filter((e) => (activeQuery.data ?? []).some((a) => a.id === e.id))}
          selectedIds={(activeQuery.data ?? []).map((a) => a.id)}
          onToggle={() => {}}
          disabled
        />
      )}

      {hasClinicServices && (
        <Link
          to="/procedimentos"
          className="flex items-center justify-between gap-3 rounded-lg border border-zinc-200 px-4 py-3 text-sm hover:bg-zinc-50 dark:border-border dark:hover:bg-muted"
        >
          <span>
            <span className="block font-medium text-zinc-900 dark:text-foreground">Procedimentos e preços</span>
            <span className="block text-[12.5px] text-zinc-500 dark:text-muted-foreground">Preço, duração e cor de cada procedimento ficam na página Procedimentos.</span>
          </span>
          <ChevronRight className="h-4 w-4 shrink-0 text-zinc-400" />
        </Link>
      )}
    </div>
  );
}
